import { useEffect, useState, type ReactNode } from 'react';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Select } from '../components/ui';
import { LineCard, type Point } from '../components/Chart';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface SeriesPoint { timestamp: string; value: number }
type SeriesKey = 'SyncDurationSeconds' | 'SyncRowsWritten' | 'SyncCyclesSkipped' | 'SyncDynamoErrors' | 'SyncBatchFallbacks';
interface SyncLatest { durationSeconds: number; rowsWritten: number; cyclesSkipped: number; dynamoErrors: number; batchFallbacks: number }
interface SyncLog { timestamp: string; message: string }
interface SyncReport {
  // Campos opcionais: o CloudWatch pode não devolver uma métrica no período.
  series?: Partial<Record<SeriesKey, SeriesPoint[]>>;
  latest?: Partial<SyncLatest>;
  logs?: SyncLog[];
}

interface InstanceTypeCount { type: string; count: number }
interface AsgInstance { id: string; type: string; health: string; state: string; az: string }
interface AsgGroup {
  name: string;
  desired: number; min: number; max: number; running: number;
  instanceTypes?: InstanceTypeCount[];
  instances?: AsgInstance[];
  cpu?: { average: number; max: number };
  network?: { inBytes: number; outBytes: number };
}
interface Infrastructure { groups?: AsgGroup[] }

interface AvgMax { average: number; max: number }
interface OpenSearchStore {
  // Tudo opcional: o CloudWatch pode não devolver a métrica no período.
  domain?: string;
  status?: string;
  nodes?: number;
  cpu?: AvgMax;
  jvmMemoryPressure?: AvgMax;
  sysMemory?: { average: number };
  storage?: { freeMB: number; usedMB: number };
  documents?: { searchable: number; deleted: number };
  shards?: number;
  throughput?: { readBytesPerSec: number; writeBytesPerSec: number };
  rates?: { search: number; indexing: number };
  latency?: { searchMs: number; indexingMs: number };
  requests?: { '2xx'?: number; '4xx'?: number; '5xx'?: number };
  rejected?: { write: number; search: number };
}
interface PostgresStore {
  id: string;
  engine?: string;
  version?: string;
  status?: string;
  instanceClass?: string;
  cpu?: AvgMax;
  connections?: AvgMax;
  freeableMemoryMB?: number;
  serverlessCapacity?: AvgMax | null;
  network?: { receiveBytesPerSec: number; transmitBytesPerSec: number };
  iops?: { read: number; write: number };
  latency?: { readMs: number; writeMs: number };
}
interface DynamoStore {
  table: string;
  itemCount?: number;
  sizeBytes?: number;
  billingMode?: string;
  consumed?: { read: number; write: number };
  throttled?: { read: number; write: number; requests: number };
  cost?: { total?: number; read?: number; write?: number; storage?: number } | null;
}
interface CostService {
  service: string;
  amount: number;
  // Mês anterior no mesmo intervalo de dias; pode faltar em serviço novo.
  previous?: number;
  deltaPct?: number;
}
interface CostReport {
  month?: string;
  periodLabel?: string;
  total?: number;
  previousTotal?: number;
  services?: CostService[];
}
interface Datastores {
  opensearch?: OpenSearchStore | null;
  postgres?: PostgresStore[];
  dynamodb?: DynamoStore[];
  costs?: CostReport | null;
}

const PERIODS = ['1h', '3h', '12h', '1d'];
/** Abaixo disso o serviço vai para a linha "Other" — não vale uma linha própria. */
const COST_MIN_LISTED = 0.5;
const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const n = (v: unknown) => Number(v) || 0;

/** HH:MM local para o eixo X dos gráficos. */
function hm(ts: string): string {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Hora local do log; sem quebrar se o timestamp vier inválido. */
function localTime(ts: string): string {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
}

/** Bytes → KB/MB/GB legível (1 casa). */
function fmtBytes(v: unknown): string {
  const b = n(v);
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${b.toFixed(0)} B`;
}

/** Verde até 60%, âmbar até 80%, vermelho acima. */
function cpuTone(pct: number): string {
  if (pct > 80) return 'bg-rose-500';
  if (pct > 60) return 'bg-amber-500';
  return 'bg-emerald-500';
}

/** JVM pressure é mais sensível: acima de 75% já é zona de OOM. */
function jvmTone(pct: number): string {
  if (pct > 75) return 'bg-rose-500';
  if (pct > 60) return 'bg-amber-500';
  return 'bg-emerald-500';
}

/** Megabytes → tamanho legível (reaproveita o fmtBytes). */
function fmtMB(v: unknown): string {
  return fmtBytes(n(v) * 1024 ** 2);
}

/** Número com separador de milhar e no máximo 1 casa. */
function fmtNum(v: unknown): string {
  return n(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** Latência em milissegundos. */
function fmtMs(v: unknown): string {
  return `${n(v).toFixed(2)} ms`;
}

/** Cluster status do OpenSearch → tom do Badge. */
function clusterTone(status?: string): 'green' | 'amber' | 'red' | 'neutral' {
  if (status === 'green') return 'green';
  if (status === 'yellow') return 'amber';
  if (status === 'red') return 'red';
  return 'neutral';
}

/** Status do cluster RDS → tom do Badge. */
function dbTone(status?: string): 'green' | 'neutral' | 'amber' {
  if (status === 'available') return 'green';
  if (status === 'stopped') return 'neutral';
  return 'amber';
}

/** Nome amigável do billing mode do DynamoDB (igual ao console da AWS). */
function billingLabel(mode?: string): string {
  if (mode === 'PAY_PER_REQUEST') return 'On-demand';
  if (mode === 'PROVISIONED') return 'Provisioned';
  return mode || '—';
}

/** Os nomes de serviço da AWS são longos demais para a lista; encurtamos os conhecidos. */
const SERVICE_LABELS: Record<string, string> = {
  'Amazon OpenSearch Service': 'OpenSearch',
  'Amazon Relational Database Service': 'RDS / Aurora',
  'Amazon Elastic Compute Cloud - Compute': 'EC2 Compute',
  'Amazon Elastic Load Balancing': 'Load Balancing',
  'Amazon DynamoDB': 'DynamoDB',
  'Amazon Simple Storage Service': 'S3',
  'Amazon Elastic Container Service': 'ECS',
  'AmazonCloudWatch': 'CloudWatch',
  'EC2 - Other': 'EC2 Other',
  Tax: 'Tax',
};

/** Nome curto do serviço; sem mapeamento, mantém o nome original da AWS. */
function serviceLabel(name: string): string {
  return SERVICE_LABELS[name] || name;
}

/** Variação vs mês anterior. Sem base de comparação vira "new" — dividir por zero não gera número útil. */
function CostDelta({ amount, previous, deltaPct }: { amount: number; previous?: number; deltaPct?: number }) {
  const base = n(previous);
  if (base <= 0) return <span className="text-slate-400">new</span>;
  const pct = deltaPct === undefined ? ((amount - base) / base) * 100 : n(deltaPct);
  // Abaixo de 0,05% arredonda para 0,0% — seta colorida ali só engana.
  if (Math.abs(pct) < 0.05) return <span className="text-slate-400">0.0%</span>;
  const up = pct > 0;
  return (
    <span className={up ? 'text-rose-600' : 'text-emerald-600'}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

/** Linha da lista de custos — mesma barra do Meter, mas proporcional ao maior valor da lista. */
function CostRow({ label, title, amount, total, max, delta }: {
  label: string; title: string; amount: number; total: number; max: number; delta: ReactNode;
}) {
  const share = total > 0 ? (amount / total) * 100 : 0;
  const width = max > 0 ? (amount / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3" title={title}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="truncate text-sm text-ink-900">{label}</span>
          <span className="shrink-0 text-xs tabular-nums">
            <span className="font-semibold text-ink-900">{usd(amount)}</span>
            <span className="text-slate-400"> · {share.toFixed(1)}%</span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div className="h-full bg-sky-500" style={{ width: `${Math.min(100, Math.max(0, width))}%` }} />
        </div>
      </div>
      <div className="w-20 shrink-0 text-right text-xs tabular-nums">{delta}</div>
    </div>
  );
}

function Tile({ value, label, sub, tone }: { value: string; label: string; sub?: string; tone: string }) {
  return (
    <div className={`relative rounded-xl p-5 text-white ${tone}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-1">{label}</div>
      {sub && <div className="text-xs opacity-90">{sub}</div>}
    </div>
  );
}

/** Barra de percentual — mesmo padrão usado no CPU do Auto Scaling. */
function Meter({ label, value, peak, tone }: { label: string; value: number; peak?: number; tone: (pct: number) => string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span className="text-slate-600 font-medium">
          {value.toFixed(1)}%
          {peak !== undefined && <span className="text-slate-400 font-normal"> · peak {peak.toFixed(1)}%</span>}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className={`h-full ${tone(value)}`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
    </div>
  );
}

/** Métrica pequena em caixa — usada nas seções de datastores. */
function Metric({ label, value, sub, big, tone }: { label: string; value: string; sub?: string; big?: boolean; tone?: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-0.5 font-semibold ${big ? 'text-xl' : 'text-sm'} ${tone || 'text-ink-900'}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function Metrics() {
  const [period, setPeriod] = useState('3h');

  const [sync, setSync] = useState<SyncReport | null>(null);
  const [syncLoading, setSyncLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);

  const [groups, setGroups] = useState<AsgGroup[]>([]);
  const [infraLoading, setInfraLoading] = useState(true);
  const [infraError, setInfraError] = useState<string | null>(null);

  const [stores, setStores] = useState<Datastores | null>(null);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState<string | null>(null);

  // Sync Report: refaz o fetch quando o período muda.
  useEffect(() => {
    let alive = true;
    setSyncLoading(true);
    api.get<ApiResponse<SyncReport>>(`/metrics/syncreport?period=${period}`)
      .then((r) => { if (!alive) return; setSync(r.data || null); setSyncError(null); })
      .catch((e) => { if (!alive) return; setSync(null); setSyncError(e instanceof ApiError ? e.message : 'Failed to load sync report'); })
      .finally(() => { if (alive) setSyncLoading(false); });
    return () => { alive = false; };
  }, [period]);

  useEffect(() => {
    let alive = true;
    setInfraLoading(true);
    api.get<ApiResponse<Infrastructure>>('/metrics/infrastructure')
      .then((r) => { if (!alive) return; setGroups(r.data?.groups || []); setInfraError(null); })
      .catch((e) => { if (!alive) return; setGroups([]); setInfraError(e instanceof ApiError ? e.message : 'Failed to load infrastructure'); })
      .finally(() => { if (alive) setInfraLoading(false); });
    return () => { alive = false; };
  }, []);

  // Uma chamada só alimenta OpenSearch + PostgreSQL + DynamoDB.
  useEffect(() => {
    let alive = true;
    setStoresLoading(true);
    api.get<ApiResponse<Datastores>>('/metrics/datastores')
      .then((r) => { if (!alive) return; setStores(r.data || null); setStoresError(null); })
      .catch((e) => { if (!alive) return; setStores(null); setStoresError(e instanceof ApiError ? e.message : 'Failed to load datastore metrics'); })
      .finally(() => { if (alive) setStoresLoading(false); });
    return () => { alive = false; };
  }, []);

  function reloadStores() {
    setStoresLoading(true);
    api.get<ApiResponse<Datastores>>('/metrics/datastores')
      .then((r) => { setStores(r.data || null); setStoresError(null); })
      .catch((e) => { setStoresError(e instanceof ApiError ? e.message : 'Failed to load datastore metrics'); })
      .finally(() => setStoresLoading(false));
  }

  function reloadSync() {
    setSyncLoading(true);
    api.get<ApiResponse<SyncReport>>(`/metrics/syncreport?period=${period}`)
      .then((r) => { setSync(r.data || null); setSyncError(null); })
      .catch((e) => { setSyncError(e instanceof ApiError ? e.message : 'Failed to load sync report'); })
      .finally(() => setSyncLoading(false));
  }

  const latest = sync?.latest;
  const errors = n(latest?.dynamoErrors) + n(latest?.batchFallbacks);
  const logs = sync?.logs || [];
  const mk = (key: SeriesKey): Point[] => (sync?.series?.[key] || []).map((p) => ({ x: hm(p.timestamp), y: n(p.value) }));

  const os = stores?.opensearch || null;
  const pgClusters = stores?.postgres || [];
  // Tabelas maiores primeiro — é por onde o olho começa.
  const dynamo = [...(stores?.dynamodb || [])].sort((a, b) => n(b.itemCount) - n(a.itemCount));
  const dynamoItems = dynamo.reduce((s, t) => s + n(t.itemCount), 0);
  const dynamoSize = dynamo.reduce((s, t) => s + n(t.sizeBytes), 0);

  // ---- Custos: maior primeiro, e o miúdo (< $0.50) vira uma linha "Other".
  const costs = stores?.costs || null;
  const costTotal = n(costs?.total);
  const costPrevTotal = n(costs?.previousTotal);
  // Sem gasto no mês anterior não há % para mostrar (divisão por zero).
  const costDeltaPct = costPrevTotal > 0 ? ((costTotal - costPrevTotal) / costPrevTotal) * 100 : null;
  const costSorted = [...(costs?.services || [])].sort((a, b) => n(b.amount) - n(a.amount));
  const costListed = costSorted.filter((s) => n(s.amount) > COST_MIN_LISTED);
  const costSmall = costSorted.filter((s) => n(s.amount) <= COST_MIN_LISTED);
  const costOther = costSmall.reduce((s, c) => s + n(c.amount), 0);
  // Inclui o "Other" no máximo para nenhuma barra passar de 100%.
  const costMax = Math.max(costOther, 0, ...costListed.map((s) => n(s.amount)));

  return (
    <Layout
      title="Metrics"
      actions={
        <Select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-24">
          {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      }
    >
      {/* ---- Sync Report ---- */}
      <h2 className="text-sm font-semibold text-ink-900 mb-3">Sync Report</h2>

      {syncError && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{syncError}</div>}

      {syncLoading && !sync ? (
        <Card className="p-10 text-center text-slate-400 mb-6">Loading…</Card>
      ) : !sync ? (
        <Card className="p-10 text-center text-slate-400 mb-6">No sync data for this period.</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <Tile value={`${n(latest?.durationSeconds).toFixed(2)}s`} label="Last Cycle Duration" sub="Most recent sync cycle" tone="bg-sky-500" />
            <Tile value={n(latest?.rowsWritten).toLocaleString()} label="Rows Written" sub="Last cycle" tone="bg-emerald-600" />
            <Tile value={n(latest?.cyclesSkipped).toLocaleString()} label="Cycles Skipped" sub="Last cycle" tone="bg-amber-500" />
            <Tile
              value={errors.toLocaleString()}
              label="Errors"
              sub={`${n(latest?.dynamoErrors)} DynamoDB · ${n(latest?.batchFallbacks)} batch fallbacks`}
              tone={errors > 0 ? 'bg-rose-500' : 'bg-emerald-600'}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <LineCard title="Sync Duration (s)" data={mk('SyncDurationSeconds')} color="#3b82f6" format={(v) => `${v.toFixed(1)}s`} />
            <LineCard title="Rows Written" data={mk('SyncRowsWritten')} color="#22c55e" />
          </div>

          <Card className="overflow-hidden mb-8">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold text-ink-900">Sync Logs</span>
              <Button variant="outline" disabled={syncLoading} onClick={reloadSync}>{syncLoading ? '…' : '↻ Refresh'}</Button>
            </div>
            <div className="bg-slate-900 max-h-96 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-slate-100">
              {logs.map((l, i) => (
                <div key={i} className="whitespace-pre-wrap break-all">
                  <span className="text-slate-500 mr-2">{localTime(l.timestamp)}</span>
                  {l.message}
                </div>
              ))}
              {logs.length === 0 && <div className="py-6 text-center text-slate-500">No log entries in this period.</div>}
            </div>
          </Card>
        </>
      )}

      {/* ---- Auto Scaling ---- */}
      <h2 className="text-sm font-semibold text-ink-900 mb-3">Auto Scaling</h2>

      {infraError && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{infraError}</div>}

      {infraLoading ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">No auto scaling groups found.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {groups.map((g) => {
            const avg = n(g.cpu?.average);
            const instances = g.instances || [];
            return (
              <Card key={g.name} className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
                  <span className="text-sm font-semibold text-ink-900">{g.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge tone={n(g.running) >= n(g.desired) ? 'green' : 'amber'}>{n(g.running)}/{n(g.desired)} running</Badge>
                    <span className="text-[11px] text-slate-400">min {n(g.min)} – max {n(g.max)}</span>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  <div className="flex flex-wrap gap-1.5">
                    {(g.instanceTypes || []).map((t) => (
                      <span key={t.type} className="inline-flex items-center rounded-md bg-surface-muted px-2 py-0.5 font-mono text-[11px] text-slate-600">
                        {n(t.count)}x {t.type}
                      </span>
                    ))}
                    {(g.instanceTypes || []).length === 0 && <span className="text-[11px] text-slate-400">No instance types reported.</span>}
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>CPU average</span>
                      <span className="text-slate-600 font-medium">{avg.toFixed(1)}% <span className="text-slate-400 font-normal">· peak {n(g.cpu?.max).toFixed(1)}%</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full ${cpuTone(avg)}`} style={{ width: `${Math.min(100, Math.max(0, avg))}%` }} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">Network In</div>
                      <div className="text-sm font-semibold text-ink-900 mt-0.5">{fmtBytes(g.network?.inBytes)}</div>
                    </div>
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">Network Out</div>
                      <div className="text-sm font-semibold text-ink-900 mt-0.5">{fmtBytes(g.network?.outBytes)}</div>
                    </div>
                  </div>

                  <div className="divide-y divide-border border-t border-border">
                    {instances.map((i) => (
                      <div key={i.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                        <span className="font-mono text-slate-600">{i.id}</span>
                        <span className="text-slate-500">{i.type}</span>
                        <span className="text-slate-400">{i.az}</span>
                        <span className="ml-auto flex items-center gap-2">
                          <span className="text-slate-400">{i.state}</span>
                          <Badge tone={i.health === 'Healthy' ? 'green' : 'red'}>{i.health}</Badge>
                        </span>
                      </div>
                    ))}
                    {instances.length === 0 && <div className="py-3 text-center text-xs text-slate-400">No instances running.</div>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ---- Datastores (OpenSearch + PostgreSQL + DynamoDB) ---- */}
      {/* Um único Refresh: as três seções vêm da mesma chamada. */}
      <div className="flex items-center justify-between gap-2 mt-8 mb-3">
        <h2 className="text-sm font-semibold text-ink-900">Datastores</h2>
        <Button variant="outline" disabled={storesLoading} onClick={reloadStores}>{storesLoading ? '…' : '↻ Refresh'}</Button>
      </div>

      {storesError && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{storesError}</div>}

      {storesLoading && !stores ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : (
        <>
          {/* ---- OpenSearch ---- */}
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">OpenSearch</h3>

          {!os ? (
            <Card className="p-6 text-center text-sm text-slate-400 mb-6">No OpenSearch domain reported.</Card>
          ) : (
            <Card className="overflow-hidden mb-6">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <span className="text-sm font-semibold text-ink-900">{os.domain || 'OpenSearch domain'}</span>
                <Badge tone={clusterTone(os.status)}>{os.status || 'unknown'} cluster</Badge>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Meter label="CPU average" value={n(os.cpu?.average)} peak={n(os.cpu?.max)} tone={cpuTone} />
                  <Meter label="JVM memory pressure" value={n(os.jvmMemoryPressure?.average)} peak={n(os.jvmMemoryPressure?.max)} tone={jvmTone} />
                  <Meter label="System memory" value={n(os.sysMemory?.average)} tone={cpuTone} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Searchable documents" value={n(os.documents?.searchable).toLocaleString()} big />
                  <Metric label="Deleted documents" value={n(os.documents?.deleted).toLocaleString()} />
                  <Metric label="Shards" value={n(os.shards).toLocaleString()} />
                  <Metric label="Nodes" value={n(os.nodes).toLocaleString()} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Free storage" value={fmtMB(os.storage?.freeMB)} />
                  <Metric label="Used storage" value={fmtMB(os.storage?.usedMB)} />
                  <Metric label="Read throughput" value={`${fmtBytes(os.throughput?.readBytesPerSec)}/s`} />
                  <Metric label="Write throughput" value={`${fmtBytes(os.throughput?.writeBytesPerSec)}/s`} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Search rate" value={fmtNum(os.rates?.search)} />
                  <Metric label="Indexing rate" value={fmtNum(os.rates?.indexing)} />
                  <Metric label="Search latency" value={fmtMs(os.latency?.searchMs)} />
                  <Metric label="Indexing latency" value={fmtMs(os.latency?.indexingMs)} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Metric label="2xx responses" value={n(os.requests?.['2xx']).toLocaleString()} />
                  <Metric label="4xx responses" value={n(os.requests?.['4xx']).toLocaleString()} tone={n(os.requests?.['4xx']) > 0 ? 'text-amber-600' : undefined} />
                  <Metric label="5xx responses" value={n(os.requests?.['5xx']).toLocaleString()} tone={n(os.requests?.['5xx']) > 0 ? 'text-rose-600' : undefined} />
                  <Metric label="Rejected writes" value={n(os.rejected?.write).toLocaleString()} tone={n(os.rejected?.write) > 0 ? 'text-rose-600' : undefined} />
                  <Metric label="Rejected searches" value={n(os.rejected?.search).toLocaleString()} tone={n(os.rejected?.search) > 0 ? 'text-rose-600' : undefined} />
                </div>
              </div>
            </Card>
          )}

          {/* ---- PostgreSQL ---- */}
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">PostgreSQL</h3>

          {pgClusters.length === 0 ? (
            <Card className="p-6 text-center text-sm text-slate-400 mb-6">No database clusters reported.</Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              {pgClusters.map((c) => {
                const stopped = c.status === 'stopped';
                const acu = c.serverlessCapacity;
                return (
                  <Card key={c.id} className="overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
                      <div>
                        <div className="text-sm font-semibold text-ink-900">{c.id}</div>
                        <div className="text-[11px] text-slate-400">
                          {[c.engine, c.version].filter(Boolean).join(' ') || 'unknown engine'}
                          {c.instanceClass ? ` · ${c.instanceClass}` : ''}
                        </div>
                      </div>
                      <Badge tone={dbTone(c.status)}>{c.status || 'unknown'}</Badge>
                    </div>

                    <div className="p-4 space-y-4">
                      {stopped && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                          Cluster is stopped — every metric below stays at zero until it is started again.
                        </div>
                      )}

                      <Meter label="CPU average" value={n(c.cpu?.average)} peak={n(c.cpu?.max)} tone={cpuTone} />

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Metric label="Connections" value={fmtNum(c.connections?.average)} sub={`peak ${fmtNum(c.connections?.max)}`} />
                        <Metric label="Freeable memory" value={fmtMB(c.freeableMemoryMB)} />
                        {acu && <Metric label="Serverless capacity" value={`${n(acu.average).toFixed(1)} ACU`} sub={`peak ${n(acu.max).toFixed(1)} ACU`} />}
                        <Metric label="Network receive" value={`${fmtBytes(c.network?.receiveBytesPerSec)}/s`} />
                        <Metric label="Network transmit" value={`${fmtBytes(c.network?.transmitBytesPerSec)}/s`} />
                        <Metric label="Read IOPS" value={fmtNum(c.iops?.read)} />
                        <Metric label="Write IOPS" value={fmtNum(c.iops?.write)} />
                        <Metric label="Read latency" value={fmtMs(c.latency?.readMs)} />
                        <Metric label="Write latency" value={fmtMs(c.latency?.writeMs)} />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* ---- DynamoDB ---- */}
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">DynamoDB</h3>

          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Tables ({dynamo.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted">
                    <th className="px-4 py-2.5 font-medium">Table</th>
                    <th className="px-4 py-2.5 font-medium text-right">Items</th>
                    <th className="px-4 py-2.5 font-medium text-right">Size</th>
                    <th className="px-4 py-2.5 font-medium">Billing</th>
                    <th className="px-4 py-2.5 font-medium text-right">Read consumed</th>
                    <th className="px-4 py-2.5 font-medium text-right">Write consumed</th>
                    <th className="px-4 py-2.5 font-medium text-right">Cost (MTD)</th>
                    <th className="px-4 py-2.5 font-medium text-right">Throttled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dynamo.map((t) => {
                    const throttled = n(t.throttled?.read) + n(t.throttled?.write) + n(t.throttled?.requests);
                    return (
                      <tr key={t.table}>
                        <td className="px-4 py-2.5 font-medium text-ink-900">{t.table}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{n(t.itemCount).toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{fmtBytes(t.sizeBytes)}</td>
                        <td className="px-4 py-2.5 text-slate-500">{billingLabel(t.billingMode)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{fmtNum(t.consumed?.read)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{fmtNum(t.consumed?.write)}</td>
                        <td
                          className="px-4 py-2.5 text-right text-slate-700 tabular-nums"
                          title={t.cost ? `read ${usd(n(t.cost.read))} · write ${usd(n(t.cost.write))} · storage ${usd(n(t.cost.storage))}` : undefined}
                        >
                          {t.cost ? usd(n(t.cost.total)) : '—'}
                        </td>
                        <td className={`px-4 py-2.5 text-right ${throttled > 0 ? 'font-bold text-rose-600' : 'text-slate-400'}`}>
                          {throttled > 0
                            ? `${n(t.throttled?.read)} read · ${n(t.throttled?.write)} write · ${n(t.throttled?.requests)} req`
                            : 'None'}
                        </td>
                      </tr>
                    );
                  })}
                  {dynamo.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No DynamoDB tables found.</td></tr>}
                </tbody>
                {dynamo.length > 0 && (
                  <tfoot>
                    <tr className="bg-surface-muted text-xs font-semibold text-slate-600 border-t border-border">
                      <td className="px-4 py-2.5">Total</td>
                      <td className="px-4 py-2.5 text-right">{dynamoItems.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{fmtBytes(dynamoSize)}</td>
                      <td className="px-4 py-2.5" colSpan={4} />
                      <td className="px-4 py-2.5 text-right tabular-nums">{usd(dynamo.reduce((a, t) => a + n(t.cost?.total), 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div className="px-4 py-2 border-t border-border text-[11px] text-slate-400">
              Item count and size are refreshed by AWS about every 6 hours, so those two columns are not real time.
            </div>
          </Card>

          {/* ---- Cost by Service ---- */}
          {/* Fica aqui dentro porque vem do mesmo payload: mesmo loading, mesmo erro, mesmo Refresh. */}
          {!costs ? (
            <Card className="overflow-hidden mt-8">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">Cost by Service</div>
              <div className="p-6 text-center text-sm text-slate-400">Cost data unavailable</div>
            </Card>
          ) : (
            <Card className="overflow-hidden mt-8">
              <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink-900">Cost by Service</span>
                  {costs.month && <Badge>{costs.month}</Badge>}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold leading-none text-ink-900 tabular-nums">{usd(costTotal)}</div>
                  {costs.periodLabel && <div className="mt-1 text-[11px] text-slate-400">{costs.periodLabel}</div>}
                </div>
              </div>

              <div className="p-4">
                <div className="mb-4 text-xs">
                  {costDeltaPct === null ? (
                    <span className="text-slate-400">No spend recorded for the same period last month, so there is nothing to compare.</span>
                  ) : (
                    <>
                      <span className={`font-semibold ${costDeltaPct > 0 ? 'text-rose-600' : costDeltaPct < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {costDeltaPct > 0 ? '+' : ''}{costDeltaPct.toFixed(1)}%
                      </span>
                      <span className="text-slate-500"> vs same period last month</span>
                      <span className="text-slate-400"> · {usd(costPrevTotal)} over the same day range</span>
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  {costListed.map((s) => (
                    <CostRow
                      key={s.service}
                      label={serviceLabel(s.service)}
                      title={s.service}
                      amount={n(s.amount)}
                      total={costTotal}
                      max={costMax}
                      delta={<CostDelta amount={n(s.amount)} previous={s.previous} deltaPct={s.deltaPct} />}
                    />
                  ))}

                  {/* "Other" junta vários serviços — uma variação agregada seria mais confusa que útil. */}
                  {costOther > 0 && (
                    <CostRow
                      label="Other"
                      title={`${costSmall.length} service${costSmall.length === 1 ? '' : 's'} under ${usd(COST_MIN_LISTED)} each`}
                      amount={costOther}
                      total={costTotal}
                      max={costMax}
                      delta={<span className="text-slate-400">—</span>}
                    />
                  )}

                  {costListed.length === 0 && costOther <= 0 && (
                    <div className="py-6 text-center text-sm text-slate-400">No service-level cost data for this period.</div>
                  )}
                </div>
              </div>

              <div className="px-4 py-2 border-t border-border text-[11px] text-slate-400">
                Month-to-date spend. Last month's figures cover the same day range, not the full month, so the two are comparable.
              </div>
            </Card>
          )}
        </>
      )}
    </Layout>
  );
}
